import {
  Users,
  Calendar,
  BookOpen,
  ReceiptText,
  Settings,
  LayoutDashboard,
  Building2,
  FileText,
  GraduationCap,
  Bus,
} from 'lucide-react';
import Link from 'next/link';
import { radius, typography } from '../../styles/design-tokens';
import { Menu } from '@headlessui/react';
import { MoreVertical } from 'lucide-react';
import { useState, useEffect } from 'react';

export interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  requiredPermissions?: string[];
}

const mainMobileItems: NavItem[] = [
  {
    label: 'Dashboard',
    href: '/admin/dashboard',
    icon: <LayoutDashboard size={20} />,
  },
  {
    label: 'Students',
    href: '/admin/student-management',
    icon: <Users size={20} />,
  },
  {
    label: 'Grades',
    href: '/admin/grades',
    icon: <GraduationCap size={20} />,
  },
  {
    label: 'Team',
    href: '/admin/team',
    icon: <Building2 size={20} />,
  },
  {
    label: 'Transportation',
    href: '/admin/transportation',
    icon: <Bus size={20} />,
  },
  // {
  //   label: 'Assessments',
  //   href: '/admin/assessment',
  //   icon: <BookOpen size={20} />,
  // },
  {
    label: 'Fees',
    href: '/admin/fees',
    icon: <ReceiptText size={20} />,
  },
];

const mobileMoreItems: NavItem[] = [
  {
    label: 'Reports',
    href: '/admin/reports',
    icon: <FileText size={20} />,
  },
  {
    label: 'Settings',
    href: '/admin/settings',
    icon: <Settings size={20} />,
  },
];

// Keep original navItems for desktop view
export const navItems: NavItem[] = [...mainMobileItems, ...mobileMoreItems];

export interface NavItemsProps {
  pathname: string;
  isMobile: boolean;
  navItems?: NavItem[];
  mobileBreakpoints?: {
    sm: {
      breakpoint: number;
      moreItems: string[];
    };
    xs: {
      breakpoint: number;
      moreItems: string[];
    };
  };
}

export const NavItems: React.FC<NavItemsProps> = ({
  pathname,
  isMobile,
  mobileBreakpoints,
  navItems: customNavItems,
}) => {
  const [windowWidth, setWindowWidth] = useState(0);
  const [mounted, setMounted] = useState(false);
  const itemsToRender = customNavItems || navItems;

  useEffect(() => {
    setMounted(true);
    setWindowWidth(window.innerWidth);

    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!mounted) {
    // For SSR rendering
    // If there are very few items, show them all without More button
    if (itemsToRender.length <= 3) {
      return (
        <div className="flex justify-evenly items-center h-16 px-4 w-full">
          {itemsToRender.map((item) => (
            <Link
              key={item.label}
              href={item.href}
              className={`flex flex-col items-center justify-center py-1 ${typography.size.xs} relative ${
                pathname.includes(item.href)
                  ? 'text-[#0D373D] font-medium'
                  : 'text-[#4D4F52]'
              }`}
            >
              <div
                className={`p-1.5 ${radius.xl} transition-all duration-200 ${
                  pathname.includes(item.href)
                    ? 'bg-[#E2FDCB] scale-110 shadow-sm'
                    : 'hover:bg-[#F5F8F9]'
                }`}
              >
                {item.icon}
              </div>
              <span
                className={`mt-0.5 transition-colors duration-200 ${
                  pathname.includes(item.href)
                    ? 'text-[#0D373D] font-medium'
                    : ''
                }`}
              >
                {item.label}
              </span>
            </Link>
          ))}
        </div>
      );
    }

    // For more items, use the standard pattern with More button
    const mainItems = itemsToRender.slice(0, 6);
    const moreItems = itemsToRender.slice(6);

    return (
      <div className="flex justify-evenly items-center h-16 px-4 w-full">
        {mainItems.map((item) => (
          <Link
            key={item.label}
            href={item.href}
            className={`flex flex-col items-center justify-center py-1 ${typography.size.xs} relative ${
              pathname.includes(item.href)
                ? 'text-[#0D373D] font-medium'
                : 'text-[#4D4F52]'
            }`}
          >
            <div
              className={`p-1.5 ${radius.xl} transition-all duration-200 ${
                pathname.includes(item.href)
                  ? 'bg-[#E2FDCB] scale-110 shadow-sm'
                  : 'hover:bg-[#F5F8F9]'
              }`}
            >
              {item.icon}
            </div>
            <span
              className={`mt-0.5 transition-colors duration-200 ${
                pathname.includes(item.href) ? 'text-[#0D373D] font-medium' : ''
              }`}
            >
              {item.label}
            </span>
          </Link>
        ))}

        {moreItems.length > 0 && (
          <Menu
            as="div"
            className="relative flex flex-col items-center justify-center"
          >
            <Menu.Button
              className={`flex flex-col items-center justify-center py-1 ${typography.size.xs}`}
            >
              <div
                className={`p-1.5 ${radius.xl} hover:bg-[#F5F8F9] transition-all duration-200`}
              >
                <MoreVertical size={20} className="text-[#4D4F52]" />
              </div>
              <span className="mt-0.5 text-[#4D4F52]">More</span>
            </Menu.Button>

            <Menu.Items className="absolute bottom-full right-0 mb-2 w-48 bg-white rounded-xl shadow-lg border border-[#E4E8EA] py-1 z-10">
              {moreItems.map((item) => (
                <Menu.Item key={item.label}>
                  {({ active }) => (
                    <Link
                      href={item.href}
                      className={`
                        flex items-center gap-2 px-4 py-2
                        ${active ? 'bg-[#F5F8F9]' : ''}
                        ${pathname.includes(item.href) ? 'text-[#0D373D]' : 'text-[#4D4F52]'}
                      `}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Menu>
        )}
      </div>
    );
  }

  if (isMobile) {
    // If there are very few items, show them all without More button regardless of breakpoints
    if (itemsToRender.length <= 3) {
      return (
        <div className="flex justify-evenly items-center h-16 px-4 w-full">
          {itemsToRender.map((item) => (
            <Link
              key={item.label}
              href={item.href}
              className={`flex flex-col items-center justify-center py-1 ${typography.size.xs} relative ${
                pathname.includes(item.href)
                  ? 'text-[#0D373D] font-medium'
                  : 'text-[#4D4F52]'
              }`}
            >
              <div
                className={`p-1.5 ${radius.xl} transition-all duration-200 ${
                  pathname.includes(item.href)
                    ? 'bg-[#E2FDCB] scale-110 shadow-sm'
                    : 'hover:bg-[#F5F8F9]'
                }`}
              >
                {item.icon}
              </div>
              <span
                className={`mt-0.5 transition-colors duration-200 ${
                  pathname.includes(item.href)
                    ? 'text-[#0D373D] font-medium'
                    : ''
                }`}
              >
                {item.label}
              </span>
            </Link>
          ))}
        </div>
      );
    }

    // For cases with more than 3 items, handle breakpoints as usual
    let allItems = [...itemsToRender];
    let currentMainItems = allItems.slice(0, 6);
    let currentMoreItems = allItems.slice(6);

    // Handle responsive items
    if (
      mobileBreakpoints?.xs &&
      windowWidth < mobileBreakpoints.xs.breakpoint
    ) {
      const itemsToMove = currentMainItems.filter((item) =>
        mobileBreakpoints.xs.moreItems.includes(item.label),
      );
      currentMainItems = currentMainItems.filter(
        (item) => !mobileBreakpoints.xs.moreItems.includes(item.label),
      );
      currentMoreItems = [...itemsToMove, ...currentMoreItems];
    } else if (
      mobileBreakpoints?.sm &&
      windowWidth < mobileBreakpoints.sm.breakpoint
    ) {
      const itemsToMove = currentMainItems.filter((item) =>
        mobileBreakpoints.sm.moreItems.includes(item.label),
      );
      currentMainItems = currentMainItems.filter(
        (item) => !mobileBreakpoints.sm.moreItems.includes(item.label),
      );
      currentMoreItems = [...itemsToMove, ...currentMoreItems];
    }

    // Only show More button if there are items to display in it
    const showMoreButton = currentMoreItems.length > 0;

    return (
      <div className="flex justify-evenly items-center h-16 px-4 w-full">
        {currentMainItems.map((item) => (
          <Link
            key={item.label}
            href={item.href}
            className={`flex flex-col items-center justify-center py-1 ${typography.size.xs} relative ${
              pathname.includes(item.href)
                ? 'text-[#0D373D] font-medium'
                : 'text-[#4D4F52]'
            }`}
          >
            <div
              className={`p-1.5 ${radius.xl} transition-all duration-200 ${
                pathname.includes(item.href)
                  ? 'bg-[#E2FDCB] scale-110 shadow-sm'
                  : 'hover:bg-[#F5F8F9]'
              }`}
            >
              {item.icon}
            </div>
            <span
              className={`mt-0.5 transition-colors duration-200 ${
                pathname.includes(item.href) ? 'text-[#0D373D] font-medium' : ''
              }`}
            >
              {item.label}
            </span>
          </Link>
        ))}

        {showMoreButton && (
          <Menu
            as="div"
            className="relative flex flex-col items-center justify-center"
          >
            <Menu.Button
              className={`flex flex-col items-center justify-center py-1 ${typography.size.xs}`}
            >
              <div
                className={`p-1.5 ${radius.xl} hover:bg-[#F5F8F9] transition-all duration-200`}
              >
                <MoreVertical size={20} className="text-[#4D4F52]" />
              </div>
              <span className="mt-0.5 text-[#4D4F52]">More</span>
            </Menu.Button>

            <Menu.Items className="absolute bottom-full right-0 mb-2 w-48 bg-white rounded-xl shadow-lg border border-[#E4E8EA] py-1 z-10">
              {currentMoreItems.map((item) => (
                <Menu.Item key={item.label}>
                  {({ active }) => (
                    <Link
                      href={item.href}
                      className={`
                        flex items-center gap-2 px-4 py-2
                        ${active ? 'bg-[#F5F8F9]' : ''}
                        ${pathname.includes(item.href) ? 'text-[#0D373D]' : 'text-[#4D4F52]'}
                      `}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Menu>
        )}
      </div>
    );
  }

  // Desktop view remains unchanged but uses itemsToRender
  return (
    <div className="relative">
      <div className="flex items-center space-x-1 overflow-x-auto pb-4 scrollbar-hide">
        {itemsToRender.map((item) => (
          <Link
            key={item.label}
            href={item.href}
            className={`
              flex-shrink-0 flex items-center gap-3 px-4 py-2 ${radius.xl} ${typography.size.sm}
              ${
                pathname.includes(item.href)
                  ? 'bg-[#E2FDCB] text-[#0D373D]'
                  : 'text-[#4D4F52] hover:bg-[#F5F8F9]'
              }
            `}
          >
            {item.icon}
            {item.label}
          </Link>
        ))}
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white to-transparent pointer-events-none" />
    </div>
  );
};
